import { useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { formatHubBranchLabel } from '../../../utils/destinationType.js';
import { filterSelectClass, selectDropdownOpenClass } from '../../common/uiClasses.js';

const HubMultiSelect = ({
  hubs = [],
  value = [],
  onChange,
  placeholder = 'All Hubs',
  className = '',
  compact = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const containerRef = useRef(null);
  const selected = Array.isArray(value) ? value.map(String) : [];

  const options = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (hubs || []).filter((hub) => {
      if (!q) return true;
      const label = `${formatHubBranchLabel(hub)} ${hub.location || ''} ${hub.hub_id || ''}`.toLowerCase();
      return label.includes(q);
    });
  }, [hubs, search]);

  useEffect(() => {
    const onClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const toggle = (hubId) => {
    const id = String(hubId);
    const next = selected.includes(id)
      ? selected.filter((item) => item !== id)
      : [...selected, id];
    onChange(next);
  };

  const label = selected.length
    ? `${selected.length} hub${selected.length === 1 ? '' : 's'} selected`
    : placeholder;

  return (
    <div ref={containerRef} className={`relative min-w-0 w-full md:w-auto ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full min-w-0 ${compact ? 'sm:min-w-[140px] sm:max-w-[180px]' : 'sm:min-w-[220px]'} ${filterSelectClass} cursor-pointer flex justify-between items-center gap-2 ${
          open ? selectDropdownOpenClass : ''
        }`}
      >
        <span className="min-w-0 truncate pr-2 font-medium text-gray-700">
          {label}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-gray-700 ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 w-full sm:min-w-[220px] bg-white border border-gray-300 rounded-xl shadow-lg mt-1">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search hub..."
              autoFocus
              className="w-full px-3 py-1.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {selected.length > 0 && (
              <button
                type="button"
                onClick={() => onChange([])}
                className="w-full text-left px-3 py-2 text-xs text-blue-600 font-semibold hover:bg-blue-50 border-b border-gray-100"
              >
                All Hubs
              </button>
            )}
            {options.map((hub) => {
              const id = String(hub.id);
              return (
                <label key={id} className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-800">
                  <input
                    type="checkbox"
                    checked={selected.includes(id)}
                    onChange={() => toggle(id)}
                    className="accent-blue-500"
                  />
                  <span className="truncate">{formatHubBranchLabel(hub) || hub.location || hub.hub_id}</span>
                </label>
              );
            })}
            {options.length === 0 && (
              <div className="px-3 py-2 text-sm text-gray-400">No hubs found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HubMultiSelect;
